import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "context/AuthContext";
import { getGlobalConfig } from "services/global.service";

interface GlobalConfigContextData {
  isMenuItemEnabled: (key: string) => boolean;
  isFeatureEnabled: (key: string) => boolean;
  /** Enquanto false, as consultas acima respondem "não" por não saber ainda. */
  configLoaded: boolean;
}

interface GlobalConfigProviderProps {
  children: ReactNode;
}

const GlobalConfigContext = createContext({} as GlobalConfigContextData);

function GlobalConfigProvider({ children }: GlobalConfigProviderProps) {
  // null = config ainda não carregado (ou falhou). Não é {} para não cair
  // no fail-open antes de sabermos a configuração real.
  const [config, setConfig] = useState<Record<string, any> | null>(null);
  const { user } = useAuth();

  // Recarrega a cada login: sem isso a config lida na abertura da aba valeria
  // para sempre, e quem entrasse de novo depois de a sessão expirar continuaria
  // com o que estava configurado horas antes.
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout>;

    async function loadGlobalConfig() {
      try {
        const global = await getGlobalConfig("global");
        const documento = global?.[0];
        if (cancelled) return;

        if (documento?.menuItems) {
          setConfig(documento);
        } else {
          // Resposta sem config: mantém fechado e tenta novamente.
          throw new Error("global config sem menuItems");
        }
      } catch (error) {
        if (cancelled) return;
        // Falha (rede, token, race com o auth): reagenda nova tentativa em vez
        // de deixar o estado vazio, que revelaria todos os menus (fail-open).
        retryTimer = setTimeout(loadGlobalConfig, 3000);
      }
    }

    loadGlobalConfig();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [user?._id]);

  // Enquanto o config não carregou (null), nada é revelado — evita que menus
  // ocultos no global__c apareçam por atraso/falha na busca. Depois de carregado,
  // um item aparece a menos que esteja explicitamente false (fail-open por chave,
  // para itens novos ainda não configurados).
  function isMenuItemEnabled(key: string) {
    if (config === null) return false;
    return config.menuItems[key] !== false;
  }

  /** Mesma regra dos menus, para as chaves soltas do global__c (ex: premioPopupEnabled). */
  function isFeatureEnabled(key: string) {
    if (config === null) return false;
    return config[key] !== false;
  }

  return (
    <GlobalConfigContext.Provider
      value={{
        isMenuItemEnabled,
        isFeatureEnabled,
        configLoaded: config !== null,
      }}
    >
      {children}
    </GlobalConfigContext.Provider>
  );
}

function useGlobalConfig() {
  const context = useContext(GlobalConfigContext);
  if (context === undefined) {
    throw new Error(
      "useGlobalConfig must be used within a GlobalConfigProvider"
    );
  }
  return context;
}

export { GlobalConfigProvider, useGlobalConfig };
